import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Target, Building, Briefcase, Loader2, Upload, CheckCircle, LayoutGrid } from 'lucide-react';
import { logger } from '../utils/logger';
import { analytics } from '../utils/analytics';
import UpgradeNudgeModal from '../components/UpgradeNudgeModal';
import { API } from '../utils/apiBase';


// Resume Upload Section Component (PDF or Word .docx)
const PDFUploadSection = ({ fileInputRef, uploading, uploadResult, onUpload }) => (
  <div className="dashboard-card p-6 border-2 border-dashed border-[#e5e7eb] hover:border-[#002fa7] transition-colors">
    <div className="flex items-center justify-between mb-3">
      <h2 className="font-satoshi text-lg font-bold text-[#0f1115] flex items-center space-x-2">
        <Upload size={20} className="text-[#002fa7]" />
        <span>Upload Resume (PDF or Word)</span>
      </h2>
      {uploadResult && (
        <span className="text-xs text-[#10b981] flex items-center gap-1" data-testid="upload-success">
          <CheckCircle size={14} />
          {uploadResult.pages} page(s), {uploadResult.characters.toLocaleString()} characters extracted
        </span>
      )}
    </div>
    <p className="text-sm text-[#6b7280] mb-4">Upload a PDF or <strong>.docx</strong> file &mdash; we&rsquo;ll extract the text automatically. Max 10MB.</p>
    <input
      ref={fileInputRef}
      type="file"
      accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      onChange={onUpload}
      className="hidden"
      data-testid="pdf-file-input"
    />
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={uploading}
      className="btn-outline inline-flex items-center gap-2 text-sm disabled:opacity-50"
      data-testid="upload-pdf-btn"
    >
      {uploading ? (
        <><Loader2 size={16} className="animate-spin" /><span>Extracting text...</span></>
      ) : (
        <><Upload size={16} /><span>Choose PDF or Word File</span></>
      )}
    </button>
  </div>
);

// Resume Details Section Component
const ResumeDetailsSection = ({ formData, onChange }) => (
  <div className="dashboard-card p-6">
    <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-4 flex items-center space-x-2">
      <FileText size={20} className="text-[#002fa7]" />
      <span>Resume Details</span>
    </h2>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-[#0f1115] mb-2">Resume Title *</label>
        <input
          type="text" name="title" value={formData.title} onChange={onChange}
          className="w-full px-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors"
          placeholder="e.g., Software Engineer Resume - Google" required data-testid="resume-title"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#0f1115] mb-2">Resume Content *</label>
        <textarea
          name="content" value={formData.content} onChange={onChange} rows={12}
          className="w-full px-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors font-mono text-sm"
          placeholder={"Paste your resume content here or upload a PDF above...\n\nPROFESSIONAL SUMMARY\nExperienced software engineer with 5+ years of experience...\n\nEXPERIENCE\nSenior Software Engineer | Company Name | 2020-Present\n\u2022 Led development of microservices architecture...\n\nSKILLS\nPython, JavaScript, React, Node.js, AWS, Docker..."}
          required data-testid="resume-content"
        />
        <p className="mt-1 text-xs text-[#6b7280]">
          {formData.content.length > 0 ? `${formData.content.length.toLocaleString()} characters` : 'Tip: Include your full resume text for the best analysis'}
        </p>
      </div>
    </div>
  </div>
);

// Target Position Section Component
const TargetPositionSection = ({ formData, onChange }) => (
  <div className="dashboard-card p-6">
    <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-4 flex items-center space-x-2">
      <Target size={20} className="text-[#002fa7]" />
      <span>Target Position</span>
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-[#0f1115] mb-2">Target Job Title *</label>
        <div className="relative">
          <Target className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
          <input
            type="text" name="target_job" value={formData.target_job} onChange={onChange}
            className="w-full pl-12 pr-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors"
            placeholder="e.g., Senior Software Engineer" required data-testid="target-job"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#0f1115] mb-2">Target Company (Optional)</label>
        <div className="relative">
          <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
          <input
            type="text" name="target_company" value={formData.target_company} onChange={onChange}
            className="w-full pl-12 pr-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors"
            placeholder="e.g., Google" data-testid="target-company"
          />
        </div>
      </div>
    </div>
  </div>
);

// Job Description Matcher Section Component
const JobDescriptionSection = ({ formData, onChange }) => (
  <div className="dashboard-card p-6 border-2 border-[#002fa7]">
    <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-2 flex items-center space-x-2">
      <Briefcase size={20} className="text-[#002fa7]" />
      <span>Job Description Matcher</span>
      <span className="text-xs bg-[#002fa7] text-white px-2 py-1 rounded-full ml-2">NEW</span>
    </h2>
    <p className="text-sm text-[#6b7280] mb-4">
      Paste the actual job description to get a precise match analysis with specific keyword recommendations.
    </p>
    <div>
      <label className="block text-sm font-medium text-[#0f1115] mb-2">Job Description (Optional but Recommended)</label>
      <textarea
        name="job_description" value={formData.job_description} onChange={onChange} rows={8}
        className="w-full px-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors text-sm"
        placeholder="Paste the full job description here..." data-testid="job-description"
      />
      <p className="mt-1 text-xs text-[#6b7280]">
        Adding a job description enables precise keyword matching and tailored suggestions for THIS specific job.
      </p>
    </div>
  </div>
);

// Target Length Section Component — how many pages the AI should aim for
const TargetLengthSection = ({ formData, onChange }) => (
  <div className="dashboard-card p-6">
    <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-2 flex items-center space-x-2">
      <FileText size={20} className="text-[#002fa7]" />
      <span>Target Resume Length</span>
    </h2>
    <p className="text-sm text-[#6b7280] mb-2">
      Pick your ideal length. Our AI will write tighter bullets and a more focused summary to fit exactly. Industry standard is 2 pages — pick longer only if you have extensive senior/executive experience.
    </p>
    <p className="text-xs text-[#9ca3af] italic mb-4">
      Note: Ascend does its best to match your target length. If your source resume has a lot of content, the final download
      may spill slightly onto the next page — we prioritize keeping your experience intact over hard-truncating.
    </p>
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="target-pages-picker">
      {[
        { value: 1, label: '1 page', hint: 'Concise' },
        { value: 2, label: '2 pages', hint: 'Standard' },
        { value: 3, label: '3 pages', hint: 'Senior' },
        { value: 4, label: '4 pages', hint: 'Executive' },
        { value: 5, label: '5 pages', hint: 'Academic/CV' },
      ].map(opt => {
        const active = Number(formData.target_pages) === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ target: { name: 'target_pages', value: opt.value } })}
            className={`p-3 text-center border-2 transition-colors ${active ? 'border-[#002fa7] bg-[#002fa7]/5 text-[#002fa7]' : 'border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7]'}`}
            data-testid={`target-pages-${opt.value}`}
          >
            <div className="font-bold">{opt.label}</div>
            <div className="text-xs opacity-75">{opt.hint}</div>
            {opt.value === 2 && <div className="text-[10px] text-[#002fa7] font-bold mt-1">DEFAULT</div>}
          </button>
        );
      })}
    </div>
  </div>
);

const CreateResume = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    target_job: '',
    target_company: '',
    job_description: '',
    target_pages: 2,
  });

  // Pre-fill from template or job board
  useEffect(() => {
    if (location.state?.fromTemplate) {
      setFormData((prev) => ({
        ...prev,
        title: location.state.title || '',
        content: location.state.content || '',
        target_job: location.state.target_job || '',
      }));
    } else if (location.state?.fromJobBoard) {
      setFormData((prev) => ({
        ...prev,
        title: location.state.title || '',
        target_job: location.state.target_job || '',
        target_company: location.state.target_company || '',
        job_description: location.state.job_description || '',
      }));
    }
  }, [location.state]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePDFUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const lower = file.name.toLowerCase();
    const isPdf = lower.endsWith('.pdf');
    const isDocx = lower.endsWith('.docx');
    const isLegacyDoc = lower.endsWith('.doc') && !isDocx;
    if (!isPdf && !isDocx) {
      setError(
        isLegacyDoc
          ? 'Legacy .doc files are not supported — please re-save as .docx in Word and try again.'
          : 'Only PDF and Word (.docx) files are supported.',
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum 10MB.');
      return;
    }

    setUploading(true);
    setError('');
    setUploadResult(null);

    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/resumes/upload-pdf`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setFormData((prev) => ({
        ...prev,
        content: res.data.extracted_text,
        title: prev.title || file.name.replace(/\.(pdf|docx|doc)$/i, ''),
      }));
      setUploadResult({ pages: res.data.pages, characters: res.data.characters });
    } catch (err) {
      logger.error('Resume upload failed:', err);
      setError(err.response?.data?.detail || 'Failed to extract text from file.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/resumes`, formData, { withCredentials: true });
      analytics.createResume(formData.target_job);
      if (typeof response?.data?.ats_score === 'number') {
        analytics.analyzeResume(response.data.ats_score);
      }
      // Auto-log an Application Tracker entry when the resume was tailored for a specific job
      if (location.state?.fromJobBoard && response?.data?.resume_id) {
        try {
          await axios.post(`${API}/applications/auto-log`, {
            resume_id: response.data.resume_id,
            job_title: formData.target_job || location.state.target_job || '',
            company: formData.target_company || location.state.target_company || '',
            job_url: location.state.job_url || '',
            source: 'job_board',
          }, { withCredentials: true });
        } catch (logErr) {
          logger.error('auto-log failed (non-fatal):', logErr);
        }
      }
      navigate(`/resume/${response.data.resume_id}`);
    } catch (err) {
      if (err.response?.status === 403) {
        setError(err.response.data.detail);
        setShowUpgrade(true);
        if (window.gtag) window.gtag('event', 'hit_resume_limit');
      } else {
        setError('Failed to create resume. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20" data-testid="create-resume-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center space-x-2 text-[#4b5563] hover:text-[#002fa7] mb-4"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-satoshi text-3xl font-black tracking-tight text-[#0f1115]">
                Create New Resume
              </h1>
              <p className="text-[#4b5563] mt-1">
                Enter your resume details for AI-powered ATS analysis
              </p>
            </div>
            <Link
              to="/templates"
              className="btn-outline inline-flex items-center gap-2 text-sm"
              data-testid="browse-templates"
            >
              <LayoutGrid size={16} />
              <span>Browse Templates</span>
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-[#fef2f2] border border-[#e11d48] text-[#e11d48]" data-testid="create-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <PDFUploadSection
            fileInputRef={fileInputRef} uploading={uploading}
            uploadResult={uploadResult} onUpload={handlePDFUpload}
          />
          <ResumeDetailsSection formData={formData} onChange={handleChange} />
          <TargetPositionSection formData={formData} onChange={handleChange} />
          <TargetLengthSection formData={formData} onChange={handleChange} />
          <JobDescriptionSection formData={formData} onChange={handleChange} />

          <div className="flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="btn-outline"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <span>Analyze Resume</span>
              )}
            </button>
          </div>
        </form>
      </div>
      <UpgradeNudgeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} trigger="resume_limit" />
    </div>
  );
};

export default CreateResume;
